import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DotsThreeVertical,
  FilePdf,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  ClockCounterClockwise,
  CalendarBlank,
  X,
  Share,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatBRL, formatNum, formatDate, downloadPdf, shareTransactionPdf, initials, paletteFor, toastWithQuota } from "@/lib/gado";
import { TransactionDetailsDialog } from "@/components/TransactionDetailsDialog";

function MiniStat({ label, value }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-1 min-w-0">
      <span className="text-[9px] uppercase tracking-[0.14em] text-gado-muted font-semibold">
        {label}
      </span>
      <span className="font-mono text-[13px] font-bold text-gado-ink tabular-nums truncate max-w-full">
        {value}
      </span>
    </div>
  );
}

function TxCard({ tx, onPdf, onShare, onEdit, onDelete, onOpen }) {
  const name = tx.buyer_name || "Sem comprador";
  const meta = [tx.animal_type, tx.iron_brand && `Ferro ${tx.iron_brand}`, tx.city_district]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      data-testid={`tx-card-${tx.transaction_id}`}
      className="min-w-0 rounded-xl border border-gado-line bg-white p-3.5 shadow-sm hover:border-gado-green/40 hover:shadow-md hover:shadow-gado-green/5 transition-all cursor-pointer"
    >
      {/* Header — avatar + comprador + ações */}
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className={`h-10 w-10 shrink-0 rounded-xl grid place-items-center font-cabinet font-bold text-sm ${paletteFor(name)}`}
        >
          {initials(name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gado-ink truncate">{name}</p>
          <p className="text-[11px] text-gado-muted truncate">
            {formatDate(tx.date)}{meta && ` · ${meta}`}
          </p>
        </div>
        {tx.status === "em_andamento" && (
          <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-gado-gold bg-gado-gold/10 border border-gado-gold/30 rounded-full px-2 py-0.5">
            Em andamento
          </span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={`row-actions-${tx.transaction_id}`}
              className="p-1.5 -mr-1.5 rounded-md text-gado-muted hover:bg-gado-line/60 shrink-0"
              aria-label="Ações"
            >
              <DotsThreeVertical size={20} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onPdf} data-testid={`pdf-${tx.transaction_id}`}>
              <FilePdf size={16} className="mr-2" /> Baixar PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onShare} data-testid={`share-${tx.transaction_id}`}>
              <Share size={16} className="mr-2" /> Compartilhar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <PencilSimple size={16} className="mr-2" /> Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
              <Trash size={16} className="mr-2" /> Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats strip — estilo resumo do PDF */}
      <div className="mt-3 grid grid-cols-3 divide-x divide-gado-line/70 rounded-lg bg-gado-bg/60 py-2">
        <MiniStat label="Cabeças" value={formatNum(tx.total_quantity, 0)} />
        <MiniStat label="Arrobas" value={`${formatNum(tx.total_arrobas, 1)}@`} />
        <MiniStat label="Média/cab" value={`${formatNum(tx.avg_weight, 1)} kg`} />
      </div>

      {/* Total — caixa dourada igual ao TOTAL A PAGAR do PDF */}
      <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-gado-gold/10 border border-gado-gold/20 px-3 py-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gado-ink shrink-0">
          Total a pagar
        </span>
        <span className="font-mono text-sm font-bold text-gado-gold tabular-nums text-right min-w-0 truncate">
          {formatBRL(tx.total_amount)}
        </span>
      </div>

      {/* Ações rápidas */}
      <div className="mt-2 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onPdf}
          data-testid={`pdf-quick-${tx.transaction_id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-gado-muted hover:bg-gado-line/50 hover:text-gado-ink"
        >
          <FilePdf size={15} /> PDF
        </button>
        <button
          onClick={onShare}
          data-testid={`share-quick-${tx.transaction_id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-gado-muted hover:bg-gado-line/50 hover:text-gado-ink"
        >
          <Share size={15} /> Compartilhar
        </button>
        <button
          onClick={onEdit}
          data-testid={`edit-quick-${tx.transaction_id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-gado-green hover:bg-gado-green/5"
        >
          <PencilSimple size={15} /> Editar
        </button>
      </div>
    </div>
  );
}

export default function Historico() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("todos");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [detailsTx, setDetailsTx] = useState(null);

  const { data: txs = [], isLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => (await api.get("/transactions?limit=500")).data,
  });

  const filtered = useMemo(() => {
    const fromD = from ? new Date(from + "T00:00:00") : null;
    const toD = to ? new Date(to + "T23:59:59") : null;
    return txs.filter((t) => {
      const matchQ =
        !q ||
        [t.buyer_name, t.partner_name, t.city_district, t.animal_type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q.toLowerCase());
      const matchS = status === "todos" || t.status === status;
      const d = new Date(t.date);
      const matchFrom = !fromD || d >= fromD;
      const matchTo = !toD || d <= toD;
      return matchQ && matchS && matchFrom && matchTo;
    });
  }, [txs, q, status, from, to]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, t) => ({
        qtd: acc.qtd + (t.total_quantity || 0),
        arrobas: acc.arrobas + (t.total_arrobas || 0),
        valor: acc.valor + (t.total_amount || 0),
      }),
      { qtd: 0, arrobas: 0, valor: 0 }
    );
  }, [filtered]);

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/transactions/${toDelete.transaction_id}`);
      qc.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Pesagem excluída");
    } catch {
      toast.error("Erro ao excluir");
    } finally {
      setToDelete(null);
    }
  };

  const pdf = async (t) => {
    const name = `Pesagem_${(t.buyer_name || "Gado").replace(/\s+/g, "_")}_${formatDate(t.date).replace(/\//g, "-")}.pdf`;
    toastWithQuota(downloadPdf(t.transaction_id, name), {
      loading: "Gerando PDF…",
      success: "PDF baixado",
      error: "Falha ao gerar PDF",
    });
  };

  const share = async (t) => {
    toastWithQuota(shareTransactionPdf(t), {
      loading: "Preparando PDF…",
      success: (r) => {
        if (r.shared) return "Compartilhado";
        if (r.cancelled) return "Compartilhamento cancelado";
        return "PDF baixado (compartilhamento indisponível neste dispositivo)";
      },
      error: "Falha ao gerar PDF",
    });
  };

  return (
    <div className="space-y-6" data-testid="historico-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Histórico</h1>
        <p className="text-gado-muted">Todas as pesagens registradas.</p>
      </div>

      <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted" />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por comprador, cidade, tipo…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:flex-none min-w-0">
            <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted pointer-events-none" />
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="pl-9 w-full min-w-0 md:w-[160px]" data-testid="filter-from" />
          </div>
          <span className="text-gado-muted text-sm shrink-0">até</span>
          <div className="relative flex-1 md:flex-none min-w-0">
            <CalendarBlank size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted pointer-events-none" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="pl-9 w-full min-w-0 md:w-[160px]" data-testid="filter-to" />
          </div>
          {(from || to) && (
            <button onClick={() => { setFrom(""); setTo(""); }} data-testid="clear-dates" className="p-2 rounded-md hover:bg-gado-line/60 text-gado-muted shrink-0">
              <X size={16} />
            </button>
          )}
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            <SelectItem value="finalizada">Finalizada</SelectItem>
            <SelectItem value="em_andamento">Em andamento</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Totals summary of the current filter — Valor ocupa largura total no mobile */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="filter-summary">
        <div className="rounded-lg border border-gado-line bg-white px-4 py-3 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Pesagens</p>
          <p className="font-cabinet font-bold text-gado-ink text-base sm:text-lg tabular-nums truncate">{filtered.length}</p>
        </div>
        <div className="rounded-lg border border-gado-line bg-white px-4 py-3 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Arrobas</p>
          <p className="font-cabinet font-bold text-gado-ink text-base sm:text-lg tabular-nums truncate">{formatNum(totals.arrobas, 1)} @</p>
        </div>
        <div className="rounded-lg border border-gado-gold/25 bg-gado-gold/10 px-4 py-3 min-w-0 col-span-2 md:col-span-1">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Valor</p>
          <p className="font-cabinet font-bold text-gado-gold text-base sm:text-lg tabular-nums truncate" data-testid="summary-valor">{formatBRL(totals.valor)}</p>
        </div>
      </div>

      {/* Cards estilo mini-relatório PDF — 1 col mobile, 2 col tablet, 3 col desktop */}
      {isLoading && (
        <div className="text-center py-10 text-gado-muted text-sm">Carregando…</div>
      )}
      {!isLoading && filtered.length === 0 && (
        <div className="rounded-xl border border-gado-line bg-white text-center py-14">
          <ClockCounterClockwise size={34} className="mx-auto text-gado-line mb-2" />
          <p className="text-gado-muted text-sm">Nenhuma pesagem encontrada.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="historico-card-grid">
        {filtered.map((t) => (
          <TxCard
            key={t.transaction_id}
            tx={t}
            onPdf={() => pdf(t)}
            onShare={() => share(t)}
            onEdit={() => navigate(`/app/nova-pesagem?id=${t.transaction_id}`)}
            onDelete={() => setToDelete(t)}
            onOpen={() => setDetailsTx(t)}
          />
        ))}
      </div>

      <TransactionDetailsDialog
        tx={detailsTx}
        open={!!detailsTx}
        onClose={() => setDetailsTx(null)}
        onEdit={() => {
          const id = detailsTx?.transaction_id;
          setDetailsTx(null);
          if (id) navigate(`/app/nova-pesagem?id=${id}`);
        }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pesagem?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A pesagem de{" "}
              <strong>{toDelete?.buyer_name || "gado"}</strong> será removida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-button" className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
